import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { StarRating } from '@/components/marketplace/StarRating';
import { ownerListReviews, ownerModerateReview, apiErrorMessage } from '@/lib/api';
import { ADMIN_PHOTOGRAPHERS as T } from '@/constants/testIds';

/** LXRA owner moderation: hide/unhide any studio's reviews. */
export const AdminReviewsDialog = ({ studio, open, onOpenChange }) => {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    if (open && studio) ownerListReviews(studio.id).then(setReviews).catch(() => setReviews([]));
    if (!open) setReviews(null);
  }, [open, studio]);

  const toggleHidden = async (r) => {
    try {
      const updated = await ownerModerateReview(r.id, { hidden: !r.hidden });
      setReviews((cur) => cur.map((x) => (x.id === r.id ? updated : x)));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update this review.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={T.reviewsDialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-[0.04em]">{studio?.name} — Reviews</DialogTitle></DialogHeader>
        {!reviews && <Skeleton className="h-40 rounded-xl bg-[#141416]" />}
        {reviews?.length === 0 && <p className="text-sm text-[hsl(var(--lxra-muted))] py-6 text-center">No reviews yet.</p>}
        {reviews?.length > 0 && (
          <ul className="space-y-4">
            {reviews.map((r) => (
              <li key={r.id} data-testid={T.reviewRow(r.id)} className="border-t border-[hsl(var(--lxra-border))] pt-4 first:border-0 first:pt-0">
                <div className="flex items-center justify-between gap-3">
                  <StarRating value={r.rating} size="h-3.5 w-3.5" />
                  <div className="flex items-center gap-2">
                    {r.ai_moderation?.flagged && (
                      <Badge data-testid={`admin-review-ai-flag-${r.id}`} variant="outline" className="text-[10px] border-red-500/30 bg-red-500/10 text-red-300">
                        <ShieldAlert className="h-3 w-3 mr-1" /> AI flagged
                      </Badge>
                    )}
                    <span className="text-[11px] text-[hsl(var(--lxra-muted))]">{r.hidden ? 'Hidden' : 'Visible'}</span>
                    <Switch checked={!r.hidden} onCheckedChange={() => toggleHidden(r)} data-testid={T.reviewToggle(r.id)} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
                  </div>
                </div>
                {r.comment && <p className="mt-2 text-sm text-[hsl(var(--lxra-fg))]">{r.comment}</p>}
                <p className="mt-1 text-[11px] text-[hsl(var(--lxra-muted))]">{r.client_name || 'A client'} · {new Date(r.created_at).toLocaleDateString()}</p>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
